import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ----------------------------------------------------------------------
// 1. Types & Interfaces (Slices)
// ----------------------------------------------------------------------

export interface CameraInteractionSlice {
  autoRotate: boolean;
  interactionMode: 'orbit' | 'crosshair' | 'zone' | 'time';
  timeSensitivity: number;
  returnSensitivity: number;
  cameraViewMode: 'Z' | 'X' | 'Y';
  cameraStates: Record<string, { position: [number, number, number], target: [number, number, number] }>;
  controlRotateSpeed: number;
  controlPanSpeed: number;
  controlZoomSpeed: number;
  hasInteracted: boolean;
  isInitialGlideComplete: boolean;
  isCurrentlyIdle: boolean;
  activeGesture: null | 'radial_menu' | 'long_press_back' | 'long_press_floor' | 'long_press_side' | 'cube_button' | 'time_drag';
  gestureVersion: number;
  setActiveGesture: (gesture: null | 'radial_menu' | 'long_press_back' | 'long_press_floor' | 'long_press_side' | 'cube_button' | 'time_drag') => void;
  incrementGestureVersion: () => void;
  setAutoRotate: (val: boolean) => void;
  setInteractionMode: (val: 'orbit' | 'crosshair' | 'zone' | 'time') => void;
  setTimeSensitivity: (val: number) => void;
  setReturnSensitivity: (val: number) => void;
  setCameraViewMode: (val: 'Z' | 'X' | 'Y') => void;
  setCameraStates: (val: Record<string, { position: [number, number, number], target: [number, number, number] }>) => void;
  setControlRotateSpeed: (val: number) => void;
  setControlPanSpeed: (val: number) => void;
  setControlZoomSpeed: (val: number) => void;
  setHasInteracted: (val: boolean) => void;
  setIsInitialGlideComplete: (val: boolean) => void;
  setIsCurrentlyIdle: (val: boolean) => void;
}

export interface SpatialDimensionsSlice {
  candleGraphDistance3D: number;
  floorDistance3D: number;
  sideGraphDistance3D: number;
  width3D: number;
  heightScale3D: number;
  zLimit3D: number;
  show3D: boolean;
  backwallStyle: 'line' | 'candles';
  setCandleGraphDistance3D: (val: number) => void;
  setFloorDistance3D: (val: number) => void;
  setSideGraphDistance3D: (val: number) => void;
  setWidth3D: (val: number) => void;
  setHeightScale3D: (val: number) => void;
  setZLimit3D: (val: number) => void;
  setShow3D: (val: boolean) => void;
  setBackwallStyle: (val: 'line' | 'candles') => void;
}

export interface IndicatorsSlice {
  showSMA10: boolean;
  showSMA20: boolean;
  showSMA50: boolean;
  sma10Period: number;
  sma20Period: number;
  sma50Period: number;
  setShowSMA10: (val: boolean) => void;
  setShowSMA20: (val: boolean) => void;
  setShowSMA50: (val: boolean) => void;
  setSma10Period: (val: number) => void;
  setSma20Period: (val: number) => void;
  setSma50Period: (val: number) => void;
}

export interface ThemeColorsSlice {
  colorUp: string;
  colorDown: string;
  colorIntensity: number;
  colorContrast: number;
  buyersColor: string;
  sellersColor: string;
  momentumSensitivity: number;
  lineWidth3D: number;
  setColorUp: (val: string) => void;
  setColorDown: (val: string) => void;
  setColorIntensity: (val: number) => void;
  setColorContrast: (val: number) => void;
  setBuyersColor: (val: string) => void;
  setSellersColor: (val: string) => void;
  setMomentumSensitivity: (val: number) => void;
  setLineWidth3D: (val: number) => void;
}

export interface GridAxesSlice {
  gridColor: string;
  gridOpacity: number;
  gridThickness: number;
  showGrid: boolean;
  edgesColor: string;
  edgesOpacity: number;
  titleSize: number;
  floorLabelsSize: number;
  sideLabelsSize: number;
  floorLabelsSpread: number;
  sideLabelsSpread: number;
  floorLabelsOpacity: number;
  sideLabelsOpacity: number;
  timeLabelsSize: number;
  timeLabelsOpacity: number;
  momentumLabelsSize: number;
  momentumLabelsOpacity: number;
  priceLabelsSize: number;
  priceLabelsOpacity: number;
  periodLabelsSize: number;
  periodLabelsOpacity: number;
  periodColor: string;
  periodOpacity: number;
  showPeriodSeparators: boolean;
  periodLineStyle: 'solid' | 'dashed';
  periodLineWidth: number;
  showPeriodLabels: boolean;

  setGridColor: (val: string) => void;
  setGridOpacity: (val: number) => void;
  setGridThickness: (val: number) => void;
  setShowGrid: (val: boolean) => void;
  setEdgesColor: (val: string) => void;
  setEdgesOpacity: (val: number) => void;
  setTitleSize: (val: number) => void;
  setFloorLabelsSize: (val: number) => void;
  setSideLabelsSize: (val: number) => void;
  setFloorLabelsSpread: (val: number) => void;
  setSideLabelsSpread: (val: number) => void;
  setFloorLabelsOpacity: (val: number) => void;
  setSideLabelsOpacity: (val: number) => void;
  setTimeLabelsSize: (val: number) => void;
  setTimeLabelsOpacity: (val: number) => void;
  setMomentumLabelsSize: (val: number) => void;
  setMomentumLabelsOpacity: (val: number) => void;
  setPriceLabelsSize: (val: number) => void;
  setPriceLabelsOpacity: (val: number) => void;
  setPeriodLabelsSize: (val: number) => void;
  setPeriodLabelsOpacity: (val: number) => void;
  setPeriodColor: (val: string) => void;
  setPeriodOpacity: (val: number) => void;
  setShowPeriodSeparators: (val: boolean) => void;
  setPeriodLineStyle: (val: 'solid' | 'dashed') => void;
  setPeriodLineWidth: (val: number) => void;
  setShowPeriodLabels: (val: boolean) => void;
}

export interface OpacityEffectsSlice {
  connectorsOpacity: number;
  connectorsThickness: number;
  showConnectors: boolean;
  curveTension: number;
  backdropBlur: number;
  floorChartOpacity: number;
  sideChartOpacity: number;
  backChartOpacity: number;
  floorSmaOpacity: number;
  sideSmaOpacity: number;
  backSmaOpacity: number;
  main3DOpacity: number;
  sma103DOpacity: number;
  sma203DOpacity: number;
  sma503DOpacity: number;
  floorChartLineWidth: number;
  sideChartLineWidth: number;
  backSmaLineWidth: number;
  floorSmaLineWidth: number;
  sideSmaLineWidth: number;
  showShadowFloor: boolean;
  showShadowSide: boolean;
  showMidLineWall: boolean;
  midLineWallColor: string;
  midLineWallOpacity: number;
  sideHistoryShadowStrength: number;
  historyShadowStrength: number;
  
  setConnectorsOpacity: (val: number) => void;
  setConnectorsThickness: (val: number) => void;
  setShowConnectors: (val: boolean) => void;
  setCurveTension: (val: number) => void;
  setBackdropBlur: (val: number) => void;
  setFloorChartOpacity: (val: number) => void;
  setSideChartOpacity: (val: number) => void;
  setBackChartOpacity: (val: number) => void;
  setFloorSmaOpacity: (val: number) => void;
  setSideSmaOpacity: (val: number) => void;
  setBackSmaOpacity: (val: number) => void;
  setMain3DOpacity: (val: number) => void;
  setSma103DOpacity: (val: number) => void;
  setSma203DOpacity: (val: number) => void;
  setSma503DOpacity: (val: number) => void;
  setFloorChartLineWidth: (val: number) => void;
  setSideChartLineWidth: (val: number) => void;
  setBackSmaLineWidth: (val: number) => void;
  setFloorSmaLineWidth: (val: number) => void;
  setSideSmaLineWidth: (val: number) => void;
  setShowShadowFloor: (val: boolean) => void;
  setShowShadowSide: (val: boolean) => void;
  setShowMidLineWall: (val: boolean) => void;
  setMidLineWallColor: (val: string) => void;
  setMidLineWallOpacity: (val: number) => void;
  setSideHistoryShadowStrength: (val: number) => void;
  setHistoryShadowStrength: (val: number) => void;
}

export interface UiPanelsSlice {
  showGodPanel: boolean;
  isDraggingGodSlider: boolean;
  showPerfPanel: boolean;
  activeGodTab: 'dashboard' | 'physics' | 'colors' | 'aesthetics' | 'spatial' | 'users' | 'feedbacks' | 'general';
  uiHiddenLevel: number;
  isEyeFlashing: boolean;
  radialMenuColor: string;
  buttonBaseColor: string;
  timeframe: string;
  visibleTimeframes: string[];
  lockedSliders: Record<string, boolean>;

  setShowGodPanel: (val: boolean) => void;
  setIsDraggingGodSlider: (val: boolean) => void;
  setShowPerfPanel: (val: boolean) => void;
  setActiveGodTab: (val: 'dashboard' | 'physics' | 'colors' | 'aesthetics' | 'spatial' | 'users' | 'feedbacks' | 'general') => void;
  setUiHiddenLevel: (val: number | ((prev: number) => number)) => void;
  setIsEyeFlashing: (val: boolean) => void;
  setRadialMenuColor: (val: string) => void;
  setButtonBaseColor: (val: string) => void;
  setTimeframe: (val: string) => void;
  setVisibleTimeframes: (val: string[]) => void;
  setLockedSliders: (val: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void;
}

// ----------------------------------------------------------------------
// 2. Main Store Interface
// ----------------------------------------------------------------------

export type FlowaesState = 
  CameraInteractionSlice &
  SpatialDimensionsSlice &
  IndicatorsSlice &
  ThemeColorsSlice &
  GridAxesSlice &
  OpacityEffectsSlice &
  UiPanelsSlice;

// ----------------------------------------------------------------------
// 3. Store Implementation
// ----------------------------------------------------------------------

export const useFlowaesStore = create<FlowaesState>()(
  persist(
    (set) => ({
      // --- Camera & Interaction ---
      autoRotate: true,
      interactionMode: 'orbit',
      timeSensitivity: 3,
      returnSensitivity: 3,
      cameraViewMode: 'Z',
      controlRotateSpeed: 1,
      controlPanSpeed: 1,
      controlZoomSpeed: 1,
      hasInteracted: false,
      isInitialGlideComplete: false,
      isCurrentlyIdle: false,
      activeGesture: null,
      gestureVersion: 0,
      setActiveGesture: (gesture) => set({ activeGesture: gesture }),
      incrementGestureVersion: () => set((state) => ({ gestureVersion: state.gestureVersion + 1 })),
      setAutoRotate: (val) => set({ autoRotate: val }),
      setInteractionMode: (val) => set({ interactionMode: val }),
      setTimeSensitivity: (val) => set({ timeSensitivity: val }),
      setReturnSensitivity: (val) => set({ returnSensitivity: val }),
      cameraStates: {},
      setCameraViewMode: (val) => set({ cameraViewMode: val }),
      setCameraStates: (val) => set({ cameraStates: val }),
      setControlRotateSpeed: (val) => set({ controlRotateSpeed: val }),
      setControlPanSpeed: (val) => set({ controlPanSpeed: val }),
      setControlZoomSpeed: (val) => set({ controlZoomSpeed: val }),
      setHasInteracted: (val) => set({ hasInteracted: val }),
      setIsInitialGlideComplete: (val) => set({ isInitialGlideComplete: val }),
      setIsCurrentlyIdle: (val) => set({ isCurrentlyIdle: val }),

      // --- Spatial Dimensions ---
      candleGraphDistance3D: 0.35,
      floorDistance3D: 0.25,
      sideGraphDistance3D: 0.40,
      width3D: 2200,
      heightScale3D: 1200,
      zLimit3D: 600,
      show3D: true,
      backwallStyle: 'candles',
      setCandleGraphDistance3D: (val) => set({ candleGraphDistance3D: val }),
      setFloorDistance3D: (val) => set({ floorDistance3D: val }),
      setSideGraphDistance3D: (val) => set({ sideGraphDistance3D: val }),
      setWidth3D: (val) => set({ width3D: val }),
      setHeightScale3D: (val) => set({ heightScale3D: val }),
      setZLimit3D: (val) => set({ zLimit3D: val }),
      setShow3D: (val) => set({ show3D: val }),
      setBackwallStyle: (val) => set({ backwallStyle: val }),

      // --- Indicators ---
      showSMA10: true,
      showSMA20: false,
      showSMA50: false,
      sma10Period: 10,
      sma20Period: 20,
      sma50Period: 50,
      setShowSMA10: (val) => set({ showSMA10: val }),
      setShowSMA20: (val) => set({ showSMA20: val }),
      setShowSMA50: (val) => set({ showSMA50: val }),
      setSma10Period: (val) => set({ sma10Period: val }),
      setSma20Period: (val) => set({ sma20Period: val }),
      setSma50Period: (val) => set({ sma50Period: val }),

      // --- Theme Colors ---
      colorUp: '#ffffff',
      colorDown: '#ff3366',
      colorIntensity: 1.0,
      colorContrast: 1.0,
      buyersColor: '#00ffa3',
      sellersColor: '#ff3d67',
      momentumSensitivity: 1.0,
      lineWidth3D: 2,
      setColorUp: (val) => set({ colorUp: val }),
      setColorDown: (val) => set({ colorDown: val }),
      setColorIntensity: (val) => set({ colorIntensity: val }),
      setColorContrast: (val) => set({ colorContrast: val }),
      setBuyersColor: (val) => set({ buyersColor: val }),
      setSellersColor: (val) => set({ sellersColor: val }),
      setMomentumSensitivity: (val) => set({ momentumSensitivity: val }),
      setLineWidth3D: (val) => set({ lineWidth3D: val }),

      // --- Grid & Axes ---
      gridColor: '#00d9ff',
      gridOpacity: 0.15,
      gridThickness: 1,
      showGrid: true,
      edgesColor: '#ffffff',
      edgesOpacity: 0.3,
      titleSize: 18,
      floorLabelsSize: 12,
      sideLabelsSize: 12,
      floorLabelsSpread: 1.0,
      sideLabelsSpread: 1.0,
      floorLabelsOpacity: 0.7,
      sideLabelsOpacity: 0.7,
      timeLabelsSize: 10,
      timeLabelsOpacity: 0.5,
      momentumLabelsSize: 10,
      momentumLabelsOpacity: 0.5,
      priceLabelsSize: 10,
      priceLabelsOpacity: 0.5,
      periodLabelsSize: 10,
      periodLabelsOpacity: 0.5,
      periodColor: '#ffffff',
      periodOpacity: 0.5,
      showPeriodSeparators: true,
      periodLineStyle: 'dashed',
      periodLineWidth: 1,
      showPeriodLabels: true,
      setGridColor: (val) => set({ gridColor: val }),
      setGridOpacity: (val) => set({ gridOpacity: val }),
      setGridThickness: (val) => set({ gridThickness: val }),
      setShowGrid: (val) => set({ showGrid: val }),
      setEdgesColor: (val) => set({ edgesColor: val }),
      setEdgesOpacity: (val) => set({ edgesOpacity: val }),
      setTitleSize: (val) => set({ titleSize: val }),
      setFloorLabelsSize: (val) => set({ floorLabelsSize: val }),
      setSideLabelsSize: (val) => set({ sideLabelsSize: val }),
      setFloorLabelsSpread: (val) => set({ floorLabelsSpread: val }),
      setSideLabelsSpread: (val) => set({ sideLabelsSpread: val }),
      setFloorLabelsOpacity: (val) => set({ floorLabelsOpacity: val }),
      setSideLabelsOpacity: (val) => set({ sideLabelsOpacity: val }),
      setTimeLabelsSize: (val) => set({ timeLabelsSize: val }),
      setTimeLabelsOpacity: (val) => set({ timeLabelsOpacity: val }),
      setMomentumLabelsSize: (val) => set({ momentumLabelsSize: val }),
      setMomentumLabelsOpacity: (val) => set({ momentumLabelsOpacity: val }),
      setPriceLabelsSize: (val) => set({ priceLabelsSize: val }),
      setPriceLabelsOpacity: (val) => set({ priceLabelsOpacity: val }),
      setPeriodLabelsSize: (val) => set({ periodLabelsSize: val }),
      setPeriodLabelsOpacity: (val) => set({ periodLabelsOpacity: val }),
      setPeriodColor: (val) => set({ periodColor: val }),
      setPeriodOpacity: (val) => set({ periodOpacity: val }),
      setShowPeriodSeparators: (val) => set({ showPeriodSeparators: val }),
      setPeriodLineStyle: (val) => set({ periodLineStyle: val }),
      setPeriodLineWidth: (val) => set({ periodLineWidth: val }),
      setShowPeriodLabels: (val) => set({ showPeriodLabels: val }),

      // --- Opacity & Effects ---
      connectorsOpacity: 0.3,
      connectorsThickness: 1,
      showConnectors: true,
      curveTension: 0.5,
      backdropBlur: 10,
      floorChartOpacity: 1.0,
      sideChartOpacity: 1.0,
      backChartOpacity: 1.0,
      floorSmaOpacity: 0.8,
      sideSmaOpacity: 0.8,
      backSmaOpacity: 0.8,
      main3DOpacity: 1.0,
      sma103DOpacity: 0.9,
      sma203DOpacity: 0.9,
      sma503DOpacity: 0.9,
      floorChartLineWidth: 2,
      sideChartLineWidth: 2,
      backSmaLineWidth: 2,
      floorSmaLineWidth: 2,
      sideSmaLineWidth: 2,
      showShadowFloor: true,
      showShadowSide: true,
      showMidLineWall: true,
      midLineWallColor: '#ffffff',
      midLineWallOpacity: 0.2,
      sideHistoryShadowStrength: 0.3,
      historyShadowStrength: 0.3,
      setConnectorsOpacity: (val) => set({ connectorsOpacity: val }),
      setConnectorsThickness: (val) => set({ connectorsThickness: val }),
      setShowConnectors: (val) => set({ showConnectors: val }),
      setCurveTension: (val) => set({ curveTension: val }),
      setBackdropBlur: (val) => set({ backdropBlur: val }),
      setFloorChartOpacity: (val) => set({ floorChartOpacity: val }),
      setSideChartOpacity: (val) => set({ sideChartOpacity: val }),
      setBackChartOpacity: (val) => set({ backChartOpacity: val }),
      setFloorSmaOpacity: (val) => set({ floorSmaOpacity: val }),
      setSideSmaOpacity: (val) => set({ sideSmaOpacity: val }),
      setBackSmaOpacity: (val) => set({ backSmaOpacity: val }),
      setMain3DOpacity: (val) => set({ main3DOpacity: val }),
      setSma103DOpacity: (val) => set({ sma103DOpacity: val }),
      setSma203DOpacity: (val) => set({ sma203DOpacity: val }),
      setSma503DOpacity: (val) => set({ sma503DOpacity: val }),
      setFloorChartLineWidth: (val) => set({ floorChartLineWidth: val }),
      setSideChartLineWidth: (val) => set({ sideChartLineWidth: val }),
      setBackSmaLineWidth: (val) => set({ backSmaLineWidth: val }),
      setFloorSmaLineWidth: (val) => set({ floorSmaLineWidth: val }),
      setSideSmaLineWidth: (val) => set({ sideSmaLineWidth: val }),
      setShowShadowFloor: (val) => set({ showShadowFloor: val }),
      setShowShadowSide: (val) => set({ showShadowSide: val }),
      setShowMidLineWall: (val) => set({ showMidLineWall: val }),
      setMidLineWallColor: (val) => set({ midLineWallColor: val }),
      setMidLineWallOpacity: (val) => set({ midLineWallOpacity: val }),
      setSideHistoryShadowStrength: (val) => set({ sideHistoryShadowStrength: val }),
      setHistoryShadowStrength: (val) => set({ historyShadowStrength: val }),

      // --- UI Panels & Misc ---
      showGodPanel: false,
      isDraggingGodSlider: false,
      showPerfPanel: false,
      activeGodTab: 'dashboard',
      uiHiddenLevel: 0,
      isEyeFlashing: false,
      radialMenuColor: '#0a0e27',
      buttonBaseColor: '#1a1f3c',
      timeframe: '1h',
      visibleTimeframes: ['5m', '20m', '30m', '1h', '6h', '12h', '1w', '1M', '1Y'],
      lockedSliders: {},
      setShowGodPanel: (val) => set({ showGodPanel: val }),
      setIsDraggingGodSlider: (val) => set({ isDraggingGodSlider: val }),
      setShowPerfPanel: (val) => set({ showPerfPanel: val }),
      setActiveGodTab: (val) => set({ activeGodTab: val }),
      setUiHiddenLevel: (val) => set((state) => ({ uiHiddenLevel: typeof val === 'function' ? val(state.uiHiddenLevel) : val })),
      setIsEyeFlashing: (val) => set({ isEyeFlashing: val }),
      setRadialMenuColor: (val) => set({ radialMenuColor: val }),
      setButtonBaseColor: (val) => set({ buttonBaseColor: val }),
      setTimeframe: (val) => set({ timeframe: val }),
      setVisibleTimeframes: (val) => set({ visibleTimeframes: val }),
      setLockedSliders: (val) => set((state) => ({ lockedSliders: typeof val === 'function' ? val(state.lockedSliders) : val })),
    }),
    {
      name: 'flowaes-settings-storage', // name of item in the storage (must be unique)
      // Omit volatile UI states from localStorage to avoid saving transient states like "isDraggingGodSlider" or "uiHiddenLevel"
      partialize: (state) => Object.fromEntries(
        Object.entries(state).filter(([key, value]) => 
          typeof value !== 'function' &&
          !['isDraggingGodSlider', 'uiHiddenLevel', 'isEyeFlashing', 'isInitialGlideComplete', 'isCurrentlyIdle', 'hasInteracted', 'activeGesture', 'gestureVersion'].includes(key)
        )
      ) as FlowaesState,
    }
  )
);
